import React, { useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Grid,
  Paper,
} from '@mui/material'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { makePostRequest } from 'utils/api'

import urls from 'utils/urls'

function ReviewConfirmStep({
  uploadedFile,
  fileData,
  accountInfo,
  columnMapping,
  previewData,
  setError,
  setLoading,
  onBack,
  onComplete,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [importing, setImporting] = useState(false)

  const handleConfirmImport = async () => {
    setImporting(true)
    setError(null)
    setLoading(true)

    try {
      const formData = new FormData()

      if (accountInfo.id) {
        // Import to existing account
        formData.append('file', uploadedFile)
        formData.append('NumeroCompte', accountInfo.id)

        // Send the same parsing configuration as the "new account" flow so
        // the backend uses the wizard's column mapping and cleaning rules
        formData.append('column_mapping', JSON.stringify(columnMapping))
        formData.append('header_row', fileData?.headerRow || 1)
        if (previewData) {
          formData.append('sign_logic', previewData.sign_logic)
          if (previewData.sign_column) {
            formData.append('sign_column', previewData.sign_column)
          }
        }

        const response = await makePostRequest(
          urls.addBulkOperations,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
          true
        )

        if (response.status === 201 || response.status === 200) {
          onComplete()
        } else {
          setError(response.data?.error || t('mesComptes.import.errors.importFailed')) // LAP-733 (GE022)
        }
      } else {
        // Create new account and import
        formData.append('csv_file', uploadedFile)

        // Add account information
        formData.append('Nom', accountInfo.bankData?.id || accountInfo.bankData)
        formData.append('TypeCompte', accountInfo.accountType?.id || accountInfo.accountType)
        formData.append('IBAN', accountInfo.IBAN)
        formData.append('NumeroCompte', accountInfo.NumeroCompte)
        formData.append('Devise', accountInfo.Devise)
        formData.append('DateOuverture', accountInfo.DateOuverture)
        formData.append('NumeroClient', accountInfo.NumeroClient || '')
        formData.append('CléRiB', accountInfo.CléRiB || '')

        // Add column mappings
        formData.append('column_mapping', JSON.stringify(columnMapping))
        formData.append('header_row', fileData?.headerRow || 1)

        // Add cleaning configuration
        if (previewData) {
          formData.append('sign_logic', previewData.sign_logic)
          if (previewData.sign_column) {
            formData.append('sign_column', previewData.sign_column)
          }
        }

        const response = await makePostRequest(
          '/api/Finance/POST/NewAccounts/',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
          true
        )

        // Handle new response format
        if (response.status === 201 || response.status === 200) {
          // Success - show success message with operation counts if available
          if (response.data?.message) {
            const successMsg = response.data.message
            const stats = []
            if (response.data.operations_created !== undefined) {
              stats.push(`${response.data.operations_created} operations created`)
            }
            if (response.data.operations_skipped !== undefined && response.data.operations_skipped > 0) {
              stats.push(`${response.data.operations_skipped} rows skipped`)
            }
            console.log(`${successMsg}. ${stats.join(', ')}`)
          }
          onComplete()
        } else {
          // Handle error responses
          const errorMessage =
            response.data?.error || response.data || t('mesComptes.import.errors.importFailed') // LAP-733 (GE022)
          setError(errorMessage)
        }
      }
    } catch (error) {
      console.error('Import error:', error)

      // Extract error message from response
      let errorMessage = t('mesComptes.import.errors.importGeneric') // LAP-733 (GE022)

      if (error.response?.data) {
        // New format: JSON with error field
        if (typeof error.response.data === 'object' && error.response.data.error) {
          errorMessage = error.response.data.error
        }
        // Old format: plain text
        else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      setError(errorMessage)
    } finally {
      setImporting(false)
      setLoading(false)
    }
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('mesComptes.import.step5.title') /* LAP-733 (GE022) */}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('mesComptes.import.step5.description') /* LAP-733 (GE022) */}
      </Typography>

      {/* Account Info */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('mesComptes.import.step5.accountInformation') /* LAP-733 (GE022) */}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('mesComptes.import.step5.bank') /* LAP-733 (GE022) */}
            </Typography>
            <Typography variant="body2">
              {accountInfo.bankData?.Nom || accountInfo.bankData || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('mesComptes.import.step5.accountType') /* LAP-733 (GE022) */}
            </Typography>
            <Typography variant="body2">
              {accountInfo.accountType?.TypeCompte || accountInfo.accountType || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('mesComptes.import.step5.iban') /* LAP-733 (GE022) */}
            </Typography>
            <Typography variant="body2">{accountInfo.IBAN || '—'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('mesComptes.import.step5.accountNumber') /* LAP-733 (GE022) */}
            </Typography>
            <Typography variant="body2">{accountInfo.NumeroCompte}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('mesComptes.import.step5.currency') /* LAP-733 (GE022) */}
            </Typography>
            <Typography variant="body2">{accountInfo.Devise}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">
              {t('mesComptes.import.step5.openingDate') /* LAP-733 (GE022) */}
            </Typography>
            <Typography variant="body2">{accountInfo.DateOuverture}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* File Info */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('mesComptes.import.step5.csvFileToImport') /* LAP-733 (GE022) */}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {t('mesComptes.import.step5.operationsFile') /* LAP-733 (GE022) */}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {uploadedFile?.name}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Warning */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>{t('mesComptes.import.step5.importantTitle') /* LAP-733 (GE022) */}</AlertTitle>
        <Typography variant="body2">
          {t('mesComptes.import.step5.importantBody') /* LAP-733 (GE022) */}
        </Typography>
      </Alert>

      {/* Navigation */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onBack} disabled={importing}>
          {t('mesComptes.import.back') /* LAP-733 (GE022) */}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirmImport}
          disabled={importing}
        >
          {importing
            ? t('mesComptes.import.step5.importing')
            : t('mesComptes.import.step5.confirmImport')}
          {/* LAP-733 (GE022) */}
        </Button>
      </Box>
    </Box>
  )
}

export default ReviewConfirmStep
